import { c as _c } from "react/compiler-runtime";
import type { PRReviews } from '@/generated/prisma/browser';
import type { Review } from '@/types/github';
import { useQuery } from '@tanstack/react-query';

/**
 * Serialized version of PRReviews that can be safely transmitted via JSON
 * BigInt fields (like prId and githubReviewId) are converted to strings
 */
export type SerializedPRReview = Omit<PRReviews, 'reviewId' | 'prId' | 'githubReviewId'> & {
  prId: string | null;
  githubReviewId: string;
};

/**
 * Extended Review type with cubic-specific metadata from the database
 */
export type EnrichedReview = {
  /**
   * The PRReviews database record associated with this GitHub review (if any)
   * With BigInt fields serialized to strings for JSON safety
   */
  dbReview?: SerializedPRReview;
} & Review;

/**
 * Query key for GitHub PR reviews
 */
export const githubReviewsQueryKey = (owner: string, repo: string, number: string) => ['github-reviews', owner, repo, number];

/**
 * Hook to fetch pull request reviews from Github for a specific PR
 *
 * Returns reviews enriched with database metadata such as whether the review
 * was submitted through cubic and whether it was from a bot.
 *
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param number - Pull request number
 * @returns Object containing reviews data, loading state, and error state
 */
export function useGitHubReviews(owner, repo, number) {
  const $ = _c(17);
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = async () => {
      const response = await fetch(`/inbox/api/pull-requests/${owner}/${repo}/${number}/review`, {
        method: "GET"
      });
      if (!response.ok) {
        throw new Error("Failed to fetch reviews");
      }
      const data = await response.json();
      return data.reviews;
    };
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const fetchReviews = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = githubReviewsQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = Boolean(owner && repo && number);
  let t3;
  if ($[8] !== fetchReviews || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: fetchReviews,
      enabled: t2
    };
    $[8] = fetchReviews;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data: reviews,
    isLoading,
    error,
    refetch
  } = useQuery(t3);
  let t4;
  if ($[12] !== error || $[13] !== isLoading || $[14] !== refetch || $[15] !== reviews) {
    t4 = {
      reviews,
      isLoading,
      error,
      refetch
    };
    $[12] = error;
    $[13] = isLoading;
    $[14] = refetch;
    $[15] = reviews;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}